import api from '@/lib/api';
import type { ManagerRegistrationPayload } from '@/schemas/registerSchema';
import type { User } from '@/types/user.model';
import { MANAGER_FIELDS } from './manager-fields.config';
import { generateFields } from '@/api/generators-api';
import type { FieldDefinition } from '@/types/fields-api.model';

export const managerService = {
  /**
   * @param data - Manager registration data
   * @returns Promise that resolves to the created user
   */
  register: async (data: ManagerRegistrationPayload): Promise<User> => {
    const response = await api.post('/managers/register', data, {
      params: {
        fields: generateFields(MANAGER_FIELDS.FULL as FieldDefinition),
      },
    });
    return response.data;
  },

  /**
   * @param email - Manager's email
   * @param password - Manager's password
   * @returns Promise with accessToken and refreshToken
   */
  login: async (
    email: string,
    password: string,
  ): Promise<{
    accessToken: string;
    refreshToken: string;
  }> => {
    // Sent with this request only. Parking it on `api.defaults` left the
    // credentials on the instance for the rest of the session, and they went
    // out on every request made before a token existed.
    const basicAuth = btoa(`${email}:${password}`);
    const response = await api.post('/managers/token', undefined, {
      headers: { Authorization: `Basic ${basicAuth}` },
    });
    return response.data;
  },

  /**
   * @param credential - Google JWT credential token
   * @returns Promise with accessToken and refreshToken
   */
  loginWithGoogle: async (
    credential: string,
  ): Promise<{
    accessToken: string;
    refreshToken: string;
  }> => {
    const response = await api.post(
      '/managers/token/google',
      {},
      {
        headers: {
          Authorization: `Bearer ${credential}`,
        },
      },
    );
    return response.data;
  },

  /**
   * @param identityToken - Apple identity token (JWT) returned by Sign in with Apple
   * @returns Promise with accessToken and refreshToken
   */
  loginWithApple: async (
    identityToken: string,
  ): Promise<{
    accessToken: string;
    refreshToken: string;
  }> => {
    const response = await api.post(
      '/managers/token/apple',
      {},
      {
        headers: {
          Authorization: `Bearer ${identityToken}`,
        },
      },
    );
    return response.data;
  },
};
